"use client";

import { metricTiers } from "@marketing/shared/lib/bento-fixtures";
import { DeltaBadge } from "@ui/components/delta-badge";
import { cn } from "@ui/lib";
import { CHART_SERIES } from "@ui/lib/chart-palette";
import { useTranslations } from "next-intl";
import { TierChip } from "./Bento";
import { useRevealed } from "./motion";

/**
 * The three-tier prompt-tracking story in one tile: a shareable KPI, the
 * backing metrics behind it, and the raw rows underneath. Tiers cascade in
 * with staggered delays; `compact` renders the home-page teaser. Chrome
 * reads from marketing.promptTracking.widgets; sample data stays EN.
 */
export function MetricTiersBento({
	compact,
	className,
}: {
	compact?: boolean;
	className?: string;
}) {
	const t = useTranslations("marketing.promptTracking.widgets");
	const revealed = useRevealed();
	const tierClass = cn(
		"motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-out",
		revealed
			? "motion-safe:translate-y-0 motion-safe:opacity-100"
			: "motion-safe:translate-y-3 motion-safe:opacity-0",
	);
	const tierStyle = (i: number) => ({ transitionDelay: `${i * 220}ms` });
	const maxInfluence = Math.max(
		...metricTiers.backing.map((g) => g.influence),
	);

	return (
		<div className={cn("space-y-3", className)}>
			<div
				className={cn("rounded-xl border bg-background p-4", tierClass)}
				style={tierStyle(0)}
			>
				<div className="flex items-center justify-between gap-2">
					<TierChip>{t("tierShare")}</TierChip>
					<span className="text-[11px] text-muted-foreground">
						{metricTiers.kpi.caption}
					</span>
				</div>
				<p className="mt-2 text-sm text-muted-foreground">
					{metricTiers.kpi.label}
				</p>
				<p className="mt-1 flex items-center gap-2">
					<span className="font-mono text-3xl font-extrabold tabular-nums">
						{metricTiers.kpi.value.toFixed(1)}%
					</span>
					<DeltaBadge change={metricTiers.kpi.delta} />
				</p>
			</div>

			<div
				className={cn("rounded-xl border bg-background p-4", tierClass)}
				style={tierStyle(1)}
			>
				<TierChip>{t("tierDefend")}</TierChip>
				<ul className={cn("mt-3 space-y-2", compact && "space-y-1.5")}>
					{metricTiers.backing
						.slice(0, compact ? 2 : undefined)
						.map((goal) => (
							<li key={goal.goal}>
								<div className="flex items-baseline justify-between gap-2 text-xs">
									<span className="truncate font-medium">
										{goal.goal}
									</span>
									<span className="shrink-0 font-mono tabular-nums text-muted-foreground">
										{t("citationsCount", {
											count: goal.citations,
										})}{" "}
										· {goal.influence.toFixed(1)}%
									</span>
								</div>
								<div className="mt-1 h-1.5 rounded-full bg-muted">
									<div
										className="h-full rounded-full motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
										style={{
											width: revealed
												? `${(goal.influence / maxInfluence) * 100}%`
												: "0%",
											backgroundColor: CHART_SERIES[0],
											transitionDelay: "450ms",
										}}
									/>
								</div>
							</li>
						))}
				</ul>
			</div>

			<div
				className={cn("rounded-xl border bg-background p-4", tierClass)}
				style={tierStyle(2)}
			>
				<div className="flex items-center justify-between gap-2">
					<TierChip>{t("tierRaw")}</TierChip>
					<span className="rounded-md border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-bold text-muted-foreground">
						MCP
					</span>
				</div>
				<ul className="mt-3 space-y-1.5 font-mono text-[11px] leading-relaxed text-muted-foreground">
					{metricTiers.raw
						.slice(0, compact ? 1 : undefined)
						.map((row) => (
							<li key={row.citation} className="truncate">
								<span className="text-foreground/80">
									{row.date}
								</span>{" "}
								· {row.platform} · “{row.prompt}” →{" "}
								{row.citation}
							</li>
						))}
				</ul>
			</div>
		</div>
	);
}
